/**
 * Come si esprime la quantità di una riga di preventivo.
 *
 * Vive in un modulo suo, separato dal generatore PDF: quel file importa jsPDF,
 * che non è caricabile fuori da un browser, e questa logica va invece coperta
 * dai test.
 */

import { formatDecimal } from "./formatting";

/**
 * Unità di vendita di un articolo, decisa dal dato e non dalla merceologia.
 *
 * `salesUnit` è l'ENUM che il modello articolo descrive come «driver dei
 * calcoli logistici»: M2 per la ceramica, ML per i profili lineari, PZ per i
 * sanitari, e così via. Prima questa funzione lo ignorava e deduceva l'unità
 * da un elenco di categorie merceologiche più la parola «battiscopa» nel nome:
 * un preventivo di un'azienda che vende altro finiva in pezzi comunque, e una
 * categoria chiamata diversamente non veniva riconosciuta.
 */
export function boxUnitOf(item) {
  const sqm = Number(item?.sqmPerBox || 0);
  const ml = Number(item?.mlPerBox || 0);
  const salesUnit = String(item?.salesUnit || "").toUpperCase();

  // Venduto a superficie o a lunghezza: la quantità si esprime in mq/ml, non
  // in scatole. Senza il fattore per scatola si assume 1, come fanno il form e
  // l'API (COALESCE(sqmPerBox, 1)).
  if (salesUnit === "M2" || salesUnit === "MQ") {
    return { perBox: sqm > 0 ? sqm : 1, unit: "mQ", hasPerBox: sqm > 0 };
  }
  if (salesUnit === "ML") {
    return { perBox: ml > 0 ? ml : 1, unit: "mL", hasPerBox: ml > 0 };
  }
  // PZ, UD, CONF, SC, KG, PAL: quantità intera, con la sua unità di misura.
  if (salesUnit) {
    return { perBox: 0, unit: salesUnit.toLowerCase(), hasPerBox: false };
  }

  // Articoli caricati prima che `salesUnit` esistesse: i dati per scatola
  // restano l'unico indizio.
  if (sqm > 0) return { perBox: sqm, unit: "mQ", hasPerBox: true };
  if (ml > 0) return { perBox: ml, unit: "mL", hasPerBox: true };
  return { perBox: 0, unit: "pz", hasPerBox: false };
}

/* Mq/ml CHIESTI dall'operatore per una riga a scatola/confezione.
   Priorità a quelli in corso di modifica nel form (mqOverrides), poi a quelli
   persistiti sulla riga (`mqQuantity`); in mancanza di entrambi si ricava dal
   numero di scatole (righe create prima del campo dedicato).

   Non è la quantità che va in fattura: per quella serve `billedQuantity`. Qui
   resta la richiesta, che il form mostra all'operatore per spiegargli quante
   scatole intere servono per coprirla. */
export function areaQuantity(itemId, boxes, perBox, mqOverrides, persistedMq) {
  const typed = Number(mqOverrides?.[itemId]);
  if (typed > 0) return Number(typed.toFixed(2));
  const saved = Number(persistedMq);
  if (saved > 0) return Number(saved.toFixed(2));
  return Number((Number(boxes || 0) * perBox).toFixed(2));
}

/* Mq/ml FATTURATI di una riga a scatola/confezione: quelli delle scatole
   intere, non quelli chiesti.

   Chi ha bisogno di 1,2 mq si porta via comunque le due scatole da 0,96 e le
   paga entrambe: i 0,72 mq di avanzo restano al cliente, non a carico di chi
   vende. La richiesta dell'operatore serve solo a stabilire quante scatole
   servono, e torna utile qui unicamente quando le scatole non sono
   calcolabili, cioè quando l'articolo non porta i mq/ml per scatola. */
export function billedQuantity({
  boxes,
  perBox,
  hasPerBox,
  itemId,
  mqOverrides,
  persistedMq,
}) {
  const fromBoxes = Number(
    (Number(boxes || 0) * Number(perBox || 0)).toFixed(2)
  );
  if (hasPerBox) return fromBoxes;
  const typed = Number(mqOverrides?.[itemId]);
  if (typed > 0) return Number(typed.toFixed(2));
  const saved = Number(persistedMq);
  if (saved > 0) return Number(saved.toFixed(2));
  return fromBoxes;
}

/**
 * Se una riga di preventivo si conta a misura (mq/ml) o a pezzi.
 *
 * Unico criterio condiviso da form, API e generatore PDF: prima ognuno dei tre
 * decideva a modo suo — il form e l'API guardando un elenco di categorie
 * merceologiche, il generatore i dati per scatola — e quando i tre non erano
 * d'accordo la quantità si perdeva per strada.
 *
 * Comanda `salesUnit`, l'unità di vendita dell'articolo. Per le righe caricate
 * prima che quel campo esistesse resta l'indizio dei dati per scatola: anche
 * quello è un dato dell'articolo, non il nome della sua categoria.
 */
export function isSoldByMeasure(item) {
  const group = measureGroupOf(item);
  return group === "area" || group === "linear";
}

/**
 * Gruppo di misura di un articolo: `"area"` (mq), `"linear"` (ml), `"piece"`
 * (quantità intera) oppure `null` quando l'articolo non porta nessun indizio.
 *
 * Da questo dipendono l'etichetta del campo quantità, il campo in cui finisce
 * ciò che l'utente digita e la validazione del form: se il gruppo dice «pezzi»
 * e l'API si aspetta le scatole, la quantità si perde fra i due.
 *
 * Con `null` decide il chiamante — di norma ripiegando sulla categoria, che
 * resta l'ultima risorsa quando dell'articolo non si sa altro.
 */
export function measureGroupOf(item) {
  const salesUnit = String(item?.salesUnit || "").toUpperCase();
  if (salesUnit === "M2" || salesUnit === "MQ") return "area";
  if (salesUnit === "ML") return "linear";
  // PZ, UD, CONF, SC, KG, PAL: quantità intera.
  if (salesUnit) return "piece";

  if (Number(item?.sqmPerBox || 0) > 0) return "area";
  if (Number(item?.mlPerBox || 0) > 0) return "linear";
  return null;
}

/* Nota di riga che tiene traccia di quanto aveva chiesto il cliente quando la
   cifra fatturata è un'altra.

   Si fattura la scatola intera, ma la richiesta non può sparire dal documento:
   è il numero su cui il cliente ha ragionato, ed è l'unico modo per capire da
   dove esce l'importo. Quando i mq/ml chiesti coprono già scatole esatte non
   c'è niente da spiegare e la nota non compare. */
export function requestedQuantityNote({ requested, billed, boxes, unit }) {
  const asked = Number(requested || 0);
  const charged = Number(billed || 0);
  if (!(asked > 0)) return null;
  // Sotto il centesimo di mq la differenza è arrotondamento, non un avanzo.
  if (Math.abs(asked - charged) < 0.01) return null;

  const isLinear = String(unit || "").toLowerCase() === "ml";
  const count = Number(boxes || 0);
  const label =
    count === 1
      ? isLinear
        ? "confezione intera"
        : "scatola intera"
      : isLinear
      ? "confezioni intere"
      : "scatole intere";

  return `Richiesti ${formatDecimal(
    asked
  )} ${unit}: si fatturano ${count} ${label} (${formatDecimal(
    charged
  )} ${unit})`;
}
